import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type BusinessEntity = Database["public"]["Tables"]["business_entity"]["Row"];

/** Column list safe for unauthenticated public reads — excludes the internal
 *  enquiry_email recipient inbox (admin-only). Mirrors RLS column GRANT. */
const PUBLIC_COLUMNS =
  "id,is_active,legal_name,trading_name,description,website_url,phone,public_email," +
  "registered_street,registered_locality,registered_region,registered_postcode,registered_country," +
  "arrival_label,arrival_street,arrival_postcode,arrival_latitude,arrival_longitude," +
  "arrival_directions_url,arrival_map_embed_url,arrival_explanation,opening_hours," +
  "mot_station_number,mot_classes,instagram_url,linktree_url,google_review_url," +
  "google_maps_profile_url,created_at,updated_at";

/**
 * Public read of the locked business entity record (one active row).
 * Uses the publishable key against the `business_entity_public` allow-list
 * view — anon has NO grants on the raw `business_entity` table, so the
 * internal `enquiry_email` inbox column is unreachable from public code.
 * Fetch the full row via getBusinessEntityAdmin.
 */
export const getBusinessEntity = createServerFn({ method: "GET" }).handler(
  async (): Promise<Omit<BusinessEntity, "enquiry_email"> | null> => {
    const supabase = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data, error } = await supabase
      // View is not in the generated Database types; cast the builder narrowly.
      .from("business_entity_public" as unknown as "business_entity")
      .select(PUBLIC_COLUMNS)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data as unknown as Omit<BusinessEntity, "enquiry_email"> | null;
  },
);


/**
 * Super-admin-only read that includes the private `enquiry_email` recipient.
 * Used by the admin BusinessEntityManager. Authenticated session enforces RLS.
 */
export const getBusinessEntityAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BusinessEntity | null> => {
    const { supabase, userId } = context;
    const { data: isSuper } = await supabase.rpc("has_admin_role", {
      _user_id: userId,
      _role: "super_admin",
    });
    if (!isSuper) throw new Error("Forbidden: super admin only");
    const { data, error } = await supabase
      .from("business_entity")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

const updateSchema = z.object({
  id: z.string().uuid(),
  patch: z.object({
    legal_name: z.string().min(1).max(200).optional(),
    trading_name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    website_url: z.string().url().optional(),
    phone: z.string().min(3).max(50).optional(),
    public_email: z.string().email().optional(),
    enquiry_email: z.string().email().optional(),
    registered_street: z.string().min(1).max(200).optional(),
    registered_locality: z.string().min(1).max(200).optional(),
    registered_region: z.string().min(1).max(200).optional(),
    registered_postcode: z.string().min(1).max(20).optional(),
    registered_country: z.string().min(2).max(3).optional(),
    arrival_label: z.string().min(1).max(200).optional(),
    arrival_street: z.string().min(1).max(200).optional(),
    arrival_postcode: z.string().min(1).max(20).optional(),
    arrival_latitude: z.number().min(-90).max(90).optional(),
    arrival_longitude: z.number().min(-180).max(180).optional(),
    arrival_directions_url: z.string().url().optional(),
    arrival_map_embed_url: z.string().url().nullable().optional(),
    arrival_explanation: z.string().min(1).max(2000).optional(),
    opening_hours: z.array(z.record(z.string(), z.unknown())).optional(),
    mot_station_number: z.string().max(20).nullable().optional(),
    mot_classes: z.array(z.number().int().min(1).max(7)).optional(),
    instagram_url: z.string().url().nullable().optional(),
    linktree_url: z.string().url().nullable().optional(),
    google_review_url: z.string().url().nullable().optional(),
    google_maps_profile_url: z.string().url().nullable().optional(),
  }),
});

/**
 * Super-admin-only update of the locked business entity.
 * RLS already restricts to super_admin via has_admin_role; we double-check here.
 */
export const updateBusinessEntity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data) => updateSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // role gate (defence in depth — RLS already enforces)
    const { data: isSuper } = await supabase.rpc("has_admin_role", {
      _user_id: userId,
      _role: "super_admin",
    });
    if (!isSuper) throw new Error("Forbidden: super admin only");

    // snapshot for audit
    const { data: prev } = await supabase
      .from("business_entity")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();

    const { data: row, error } = await supabase
      .from("business_entity")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(data.patch as any)
      .eq("id", data.id)
      .select("*")
      .single();
    if (error) throw new Error(error.message);

    // audit log
    await supabase.from("audit_logs").insert({
      actor_id: userId,
      action: "business_entity.update",
      entity_type: "business_entity",
      entity_id: data.id,
      summary: `Updated ${Object.keys(data.patch).join(", ")}`,
      old_value: prev ?? null,
      new_value: row,
    });

    return row;
  });
